import { useCallback, useMemo } from 'react';
import { useEffectOnce } from 'react-use';
import hand from '~/assets/icons/hand.svg';
import { playSound, Sound } from '~/assets/sounds';
import { CollectionImage, CurrencyIcon } from '~/components/collections';
import { useWalletIcon } from '~/hooks';
import { rage, useObservable } from '~/rage';
import { CaseItemRarity, Currency, RewardType } from '~/shared';
import {
	setActiveCase,
	setModalCaseDefaultVisible,
	setOpenCaseRewardVisible,
	setOpenCaseVisible,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import { CaseItemColors } from '../CaseItem';
import { TextLabel } from '../TextLabel';
import {
	StyledCircle,
	StyledDecBg,
	StyledLight,
	StyledLightColor,
	StyledRewardContainer,
	StyledRewardTakeButton,
	StyledRewardTakeButtonContainer,
	StyledRewardTakeButtonLight,
	StyledRewardTextContainer,
	StyledShadow,
	StyledStar,
	StyledWrapper,
	StyledXPContainer,
} from './OpenCaseReward.styled';

const OpenCaseReward = () => {
	const dispatch = useLayoutDispatch();
	const openCaseResult = useLayoutSelector(
		(state) => state.mainMenu.openCaseResult,
	);
	const activeCase = useLayoutSelector((state) => state.mainMenu.activeCase);

	const donateItems = useObservable(rage.inventoryDonate.getDonateOwnedItems());
	const caseDonateId = activeCase?.donateItemId ?? activeCase?.id;
	const ownedCount = useMemo(
		() => donateItems.find((item) => item.id === caseDonateId)?.count || 0,
		[donateItems, caseDonateId],
	);

	const rarity = useMemo(() => {
		return (
			activeCase?.items.find((item) => item.donateItemId === openCaseResult)
				?.rarity || CaseItemRarity.COMMON
		);
	}, [activeCase?.items, openCaseResult]);

	const rubleIcon = useWalletIcon(Currency.Ruble, true);

	const handleClose = useCallback(async () => {
		if (!activeCase) {
			return;
		}

		const selectedCase = rage.cases.getLootboxById(activeCase?.id);
		const { getClientConfig: _getClientConfig, ...caseWithoutConfig } =
			selectedCase as any;
		dispatch(setActiveCase(caseWithoutConfig));

		if (ownedCount > 0) {
			dispatch(setOpenCaseRewardVisible(false));
			dispatch(setOpenCaseVisible(true));
			return;
		}

		dispatch(setOpenCaseRewardVisible(false));
		dispatch(setModalCaseDefaultVisible(true));
	}, [activeCase]);

	useEffectOnce(() => {
		playSound(Sound.SuperReward);
	});

	return (
		<StyledWrapper>
			<StyledShadow />
			<StyledLightColor
				$color={CaseItemColors[rarity] || CaseItemColors[CaseItemRarity.COMMON]}
			/>
			<StyledCircle
				$color={CaseItemColors[rarity] || CaseItemColors[CaseItemRarity.COMMON]}
			/>
			<StyledLight />
			<StyledDecBg />
			<StyledRewardContainer
				style={{
					visibility: openCaseResult ? 'visible' : 'hidden',
				}}
			>
				<StyledRewardTextContainer>
					<TextLabel style={{ fontSize: 16 }}>Ваша награда</TextLabel>
					<TextLabel style={{ fontSize: 30, fontWeight: 700 }}>
						{openCaseResult}
					</TextLabel>
					{openCaseResult && (
						<StyledXPContainer>
							<TextLabel>+</TextLabel>
							<TextLabel>
								{rage.cases.getRewardNameById(openCaseResult || '')}
							</TextLabel>
							{rage.inventoryDonate.getTypeById(openCaseResult || '') ===
							RewardType.EXP ? (
								<StyledStar />
							) : (
								<CurrencyIcon icon={rubleIcon} size="lg" />
							)}
						</StyledXPContainer>
					)}
				</StyledRewardTextContainer>
				<CollectionImage
					collection={'vehicles'}
					id={openCaseResult || ''}
					size={'lg'}
				/>
				<StyledRewardTakeButtonContainer>
					<StyledRewardTakeButton
						onClick={() => {
							handleClose();
							playSound(Sound.ClickButton);
						}}
						onMouseEnter={() => playSound(Sound.LinkCursor)}
					>
						<img src={hand} />
						<TextLabel>Забрать награду</TextLabel>
					</StyledRewardTakeButton>
					<StyledRewardTakeButtonLight />
				</StyledRewardTakeButtonContainer>
			</StyledRewardContainer>
		</StyledWrapper>
	);
};

export { OpenCaseReward };
